import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import api from "@/services/api";
import { API_ENDPOINTS } from "@/config/api";
import SettlementModal from "@/components/SettlementModal";

const LIMIT = 100;
const DEFAULT_OFFSET = 0;

export default function MatchSettlement() {
  const [activeTab, setActiveTab] = useState("MO"); // "MO" = Match Odds, "BM" = Bookmaker
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [modalMode, setModalMode] = useState("declare"); // 'declare' | 'void'
  const [selectedMatch, setSelectedMatch] = useState(null);

  const fetchMatches = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.MOMATCHES, {
        params: { limit: LIMIT, offset: DEFAULT_OFFSET },
      });
      if (response.data?.success && Array.isArray(response.data.data)) {
        setMatches(response.data.data);
      } else {
        setMatches([]);
      }
    } catch (error) {
      if (!silent) console.error("Error fetching MO/BM matches:", error);
      setMatches([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
    const interval = setInterval(() => fetchMatches(true), 2000);
    return () => clearInterval(interval);
  }, []);

  const filteredMatches = matches.filter((m) => m.gameType === activeTab);

  const handleDeclare = (row) => {
    setSelectedMatch(row);
    setModalMode("declare");
    setModalShow(true);
  };

  const handleVoid = (row) => {
    setSelectedMatch(row);
    setModalMode("void");
    setModalShow(true);
  };

  const handleSettlementSubmit = async (payload) => {
    console.log("Settlement submit", payload);
    // TODO: wire to declare/void API when available
    setModalShow(false);
    setSelectedMatch(null);
  };

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        {/* Header */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >
          <div className="flex justify-between items-center mb-1">
            <div
              style={{ color: "#cae0e8", fontSize: "24px" }}
              className="font-semibold"
            >
              Match Settlement
            </div>
            <button
              type="button"
              onClick={() => fetchMatches()}
              disabled={loading}
              className="px-4 py-2 bg-[#11374a] text-white text-[14px] font-bold hover:bg-[#1a4f66] transition-colors disabled:opacity-50"
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
          <div className="w-full h-px bg-white mb-2"></div>
        </div>

        {/* Tabs: Match Odds | Bookmaker */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px" }}
          className="shadow-sm px-4 pt-4 pb-2"
        >
          <div className="flex mb-4">
            <button
              onClick={() => setActiveTab("MO")}
              className={`px-4 py-2 text-[12px] font-bold transition-colors ${activeTab === "MO"
                ? "bg-[#333d79ff] text-white"
                : "bg-gray-600 text-white"
                }`}
            >
              Match Odds
            </button>
            <button
              onClick={() => setActiveTab("BM")}
              className={`px-4 py-2 text-[12px] font-bold transition-colors ${activeTab === "BM"
                ? "bg-[#333d79ff] text-white"
                : "bg-gray-600 text-white"
                }`}
            >
              Bookmaker
            </button>
          </div>
        </div>

        {/* Table */}
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            marginTop: "-8px",
            minHeight: "500px",
          }}
          className="shadow-sm p-4"
        >
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[20%]">
                      Match Title
                    </th>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[15%]">
                      Market Type
                    </th>
                    <th className="text-right px-4 py-2 text-white font-semibold w-[10%]">
                      Total Bets
                    </th>
                    {/* <th className="text-right px-4 py-2 text-white font-semibold w-[10%]">
                      Counts
                    </th> */}
                    <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[20%]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center text-white py-4"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : filteredMatches.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center text-white py-4"
                      >
                        No matches found
                      </td>
                    </tr>
                  ) : (
                    filteredMatches.map((row, index) => (
                      <tr
                        key={`${row.matchId}-${row.eventId}-${row.gameType}-${index}`}
                        className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                          }`}
                      >
                        <td className="py-2 px-4 text-[#2ce5dd] font-medium truncate">
                          {row.matchTitle ?? `${row.teamOne} vs ${row.teamTwo}`}
                        </td>
                        <td className="py-2 px-4 text-white font-bold truncate">
                          {row.marketType}
                        </td>
                        <td className="py-2 px-4 text-right text-white font-bold">
                          {row.totalBets ?? 0}
                        </td>
                        {/* <td className="py-2 px-4 text-right text-white font-bold">
                          {row.counts ?? 0}
                        </td> */}
                        <td className="py-2 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleDeclare(row)}
                              className="px-3 py-1 bg-[#333d79ff] text-white text-[12px] font-bold hover:bg-[#2d3569] transition-colors"
                            >
                              Declare
                            </button>
                            <button
                              onClick={() => handleVoid(row)}
                              className="px-3 py-1 bg-gray-600 text-white text-[12px] font-bold hover:bg-gray-500 transition-colors"
                            >
                              Void
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <SettlementModal
        show={modalShow}
        onHide={() => { setModalShow(false); setSelectedMatch(null); }}
        mode={modalMode}
        match={selectedMatch}
        onSubmit={handleSettlementSubmit}
      />
    </Layout>
  );
}
